<template>
  <div class="widget__content-center widget__content-center--flex">
    <div v-if="time?.name === 'Day'" class="widget__header-text">
      Showing distributed dwell for <span>{{ day?.title }}</span>
    </div>
    <div v-else class="widget__header-text">Showing total distributed dwell across event</div>

    <div v-if="chartSeries" class="chart-wrapper">
      <apexchart
        height="100%"
        width="100%"
        type="donut"
        :options="chartOptions"
        :series="chartSeries"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import type {
  WidgetFilter,
  WidgetSetting,
  WidgetTypeDistributedDwell,
  DataPoint,
} from '@/types/Widget';
import { pieColors, pieTextColors } from '@/utils/chartColors';
import type { BaseChartOptions } from '@/utils/chartSettings';
import { getChartExportSettings, getDefaultChartOptions } from '@/utils/chartSettings';
import { formatNumber } from '@/utils/formatNumbers';

const props = defineProps<{
  widget: WidgetTypeDistributedDwell;
  area?: WidgetFilter;
  time?: WidgetFilter;
  zone?: WidgetSetting;
  cluster?: WidgetSetting;
  day?: WidgetSetting;
}>();

const chartData = computed(() => {
  const areaName = props.area?.name;
  const timeName = props.time?.name;
  const dayId = props.day?.id;
  const zoneId = props.zone?.id;

  let baseData: DataPoint = {};

  if (areaName === 'Site') {
    baseData = props.widget.data['Site']['Total'];

    if (timeName === 'Day' && dayId) {
      baseData = props.widget.data['Site']['Day'][dayId];
    }
  } else if (zoneId) {
    baseData = props.widget.data['Zone'][zoneId]['Total'];

    if (timeName === 'Day' && dayId) {
      baseData = props.widget.data['Zone'][zoneId]['Day'][dayId];
    }
  }

  const formatted = Object.keys(baseData).map((key: string) => {
    const baseFilter = props.widget.filters.area;
    const siteFilter = baseFilter.find((area) => area.name === 'Site');
    const zoneFilter = baseFilter.find((area) => area.name === 'Zone');

    const siteData = props.area?.name === 'Zone' ? zoneFilter?.series : siteFilter?.series;
    const mappedSeries = siteData?.find((site) => `${site.id}` === key);
    const title = mappedSeries?.title;

    const newSeries = {
      name: title,
      data: baseData[key],
    };

    return newSeries;
  });

  return formatted;
});

const chartSeries = computed(() => {
  return chartData.value?.map((point) => point.data);
});

const chartLabels = computed(() => {
  if (chartData.value?.length) {
    return chartData.value?.map((point) => point.name || '');
  }
  return [];
});

const defaultChartOptions = getDefaultChartOptions(props.widget.code_name);

const chartOptions = ref<BaseChartOptions>({
  ...defaultChartOptions.value,
  chart: {
    ...defaultChartOptions.value.chart,
    type: 'donut',
    id: 'meshh-chart-distributed-dwell',
    toolbar: getChartExportSettings(
      props.widget.name,
      props.area?.name || '',
      props.time?.name || '',
      props.zone?.title || '',
      props.cluster?.title || '',
      '',
    ),
  },
  colors: pieColors,
  labels: chartLabels.value,
  stroke: {
    width: 0,
  },
  plotOptions: {
    pie: {
      customScale: 1,
      donut: {
        size: '50%',
      },
    },
  },
  dataLabels: {
    dropShadow: {
      enabled: false,
    },
    style: {
      colors: pieTextColors,
      fontSize: '12px',
      fontWeight: 'bold',
    },
  },
  tooltip: {
    y: {
      formatter: (val: number) => {
        return formatNumber(val, 0) + ' Visits';
      },
    },
  },
});
</script>

<style lang="scss">
.chart-wrapper {
  overflow: hidden;

  .apexcharts-canvas {
    max-width: 100%;
    max-height: 100%;
  }
}
</style>
